import { useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard, Compass, BookOpenCheck, Scale, NotebookPen, Store, ShoppingCart,
  Settings, LogOut, Menu, Search, Crown,
} from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Logo } from "@/components/lf";
import { logout } from "@/lib/auth";
import { useStore } from "@/lib/hooks";
import { toast } from "sonner";

const GROUPS = [
  { label: "RUANG KERJA", items: [
    { to: "/ringkasan", label: "Ringkasan", icon: LayoutDashboard, id: "ringkasan" },
    { to: "/kompas-buku", label: "Kompas Buku", icon: Compass, id: "kompas-buku" },
    { to: "/progres-membaca", label: "Progres Membaca", icon: BookOpenCheck, id: "progres-membaca" },
    { to: "/bedah-argumen", label: "Bedah Argumen", icon: Scale, id: "bedah-argumen" },
    { to: "/catatan", label: "Catatan Saya", icon: NotebookPen, id: "catatan" },
  ]},
  { label: "TOKO FISIK", items: [
    { to: "/toko-buku", label: "Toko Buku", icon: Store, id: "toko-buku" },
    { to: "/keranjang", label: "Keranjang / Checkout", icon: ShoppingCart, id: "keranjang" },
  ]},
  { label: "AKUN", items: [
    { to: "/pengaturan", label: "Pengaturan", icon: Settings, id: "pengaturan" },
  ]},
];

function SidebarBody({ onNavigate }) {
  const navigate = useNavigate();
  const sub = useStore("subscription");
  const cart = useStore("cart");
  const cartCount = cart.reduce((a, c) => a + c.qty, 0);

  const doLogout = () => {
    logout();
    onNavigate && onNavigate();
    toast.success("Anda telah keluar.");
    navigate("/masuk", { replace: true });
  };

  return (
    <div className="flex flex-col h-full">
      <Link to="/ringkasan" onClick={onNavigate} className="flex items-center gap-3 px-6 h-[72px] border-b border-[#29292D]" data-testid="sidebar-brand">
        <Logo size={44} />
        <div>
          <div className="font-serif text-xl leading-none text-[#F2F0EC]">Logika Filsuf</div>
          <div className="text-[10px] tracking-[0.18em] uppercase text-[#929197] mt-1">Philosophy is Easy</div>
        </div>
      </Link>
      <nav className="flex-1 overflow-y-auto px-3 py-5 space-y-6" data-testid="sidebar-nav">
        {GROUPS.map((g) => (
          <div key={g.label}>
            <div className="lf-overline px-3 mb-2">{g.label}</div>
            <ul className="space-y-0.5">
              {g.items.map(({ to, label, icon: Icon, id }) => (
                <li key={to}>
                  <NavLink
                    to={to}
                    onClick={onNavigate}
                    data-testid={`nav-${id}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm border-l-2 ${
                        isActive ? "bg-[#545B6B]/30 text-[#F2F0EC] border-[#9aa3b8]" : "text-[#929197] border-transparent hover:text-[#F2F0EC] hover:bg-[#151518]"
                      }`
                    }
                  >
                    <Icon size={17} strokeWidth={1.6} />
                    <span className="flex-1">{label}</span>
                    {id === "keranjang" && cartCount > 0 && (
                      <span className="text-[10px] bg-[#E6E2D6] text-[#0B0B0D] px-1.5 rounded-sm font-semibold">{cartCount}</span>
                    )}
                  </NavLink>
                </li>
              ))}
              {g.label === "AKUN" && (
                <li>
                  <button onClick={doLogout} data-testid="nav-keluar" className="w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm border-l-2 border-transparent text-[#929197] hover:text-[#F2F0EC] hover:bg-[#151518]">
                    <LogOut size={17} strokeWidth={1.6} /> Keluar
                  </button>
                </li>
              )}
            </ul>
          </div>
        ))}
      </nav>
      <Link to="/langganan" onClick={onNavigate} data-testid="sidebar-subscription-card" className="m-3 p-4 border border-[#29292D] bg-[#151518] rounded-md hover:border-[#545B6B] block">
        <div className="flex items-center gap-2 text-[#E6E2D6]"><Crown size={14} /><span className="lf-overline !text-[#E6E2D6]">Langganan</span></div>
        <div className="font-serif text-lg mt-1 text-[#F2F0EC]">{sub ? sub.planName : "Belum berlangganan"}</div>
        <div className="text-xs text-[#929197]">{sub ? "Aktif (demonstrasi)" : "Lihat paket tahunan (demo)"}</div>
      </Link>
    </div>
  );
}

export default function Layout() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/catatan?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <div className="min-h-screen bg-[#0B0B0D] text-[#F2F0EC]">
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-64 bg-[#101012] border-r border-[#29292D] z-30" data-testid="sidebar-desktop">
        <SidebarBody />
      </aside>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-72 p-0 bg-[#101012] border-r border-[#29292D]" data-testid="sidebar-drawer">
          <SheetTitle className="sr-only">Navigasi</SheetTitle>
          <SidebarBody onNavigate={() => setOpen(false)} />
        </SheetContent>
      </Sheet>

      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 h-16 bg-[#0B0B0D] border-b border-[#29292D] flex items-center gap-3 px-4 sm:px-8">
          <button className="lg:hidden p-2 -ml-2 text-[#F2F0EC]" onClick={() => setOpen(true)} data-testid="mobile-menu-button" aria-label="Buka navigasi">
            <Menu size={22} />
          </button>
          <form onSubmit={submitSearch} className="flex-1 max-w-md relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#929197]" />
            <input data-testid="topnav-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari catatan…" className="lf-field !pl-9 !py-2" />
          </form>
        </header>
        <main className="px-4 sm:px-8 lg:px-12 py-8 sm:py-10 max-w-[1280px]" data-testid="main-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
